import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, tap, timeout, catchError, of } from 'rxjs';
import { ProfileDetailsResponse } from './interface/profile-details-response-i';
import { BaseResponse } from './interface/base-response-i';
import { AuditLogService } from './services/audit-log.service';
import { environment } from '../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private apiUrl = environment.apiUrl;
  private readonly SESSION_KEY = 'userDetails';
  private rememberMe: boolean = true;
  private readonly AUDIT_LOG_TIMEOUT_MS = 3000;

  constructor(
    private http: HttpClient,
    private router: Router,
    private auditLogService: AuditLogService
  ) { }

  login(username: string, password: string): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/api/v2/auth/sign-in`, { username, password });
  }

  setSession(data: any, remember?: boolean): void {
    if (remember !== undefined) {
      this.rememberMe = remember;
    }

    localStorage.removeItem(this.SESSION_KEY);
    sessionStorage.removeItem(this.SESSION_KEY);

    const storage = this.rememberMe ? localStorage : sessionStorage;
    storage.setItem(this.SESSION_KEY, JSON.stringify(data));
  }

  updateSession(partial: Partial<any>): void {
    const current = this.getSession() ?? {};
    this.setSession({ ...current, ...partial });
  }

  getSession(): any {
    const session = localStorage.getItem(this.SESSION_KEY) || sessionStorage.getItem(this.SESSION_KEY);

    if (!session) {
      this.clearSession();
      return null;
    }

    try {
      return JSON.parse(session);
    } catch {
      this.clearSession();
      return null;
    }
  }

  getToken(): string | null {
    return this.getSession()?.token ?? null;
  }

  // Now token-derived — see getTokenRole(). Kept as the single public
  // read so nothing else in the app needs to know it's backed by the JWT.
  getRole(): string | null {
    return this.getTokenRole();
  }

  clearSession(): void {
    localStorage.removeItem(this.SESSION_KEY);
    sessionStorage.removeItem(this.SESSION_KEY);
  }

  isLoggedIn(): boolean {
    return !!this.getToken();
  }

  getUsername(): string {
    return this.getSession()?.userDetails?.username ?? 'User';
  }

  getProfileImageUrl(): string | null {
    return this.getSession()?.userDetails?.imageUrl ?? null;
  }

  getUserId(): number | null {
    return this.getSession()?.userDetails?.id ?? null;
  }

  // ─── Token claim decoding (security-relevant reads) ───────────────────────

  /**
   * Decodes the full JWT payload once. Returns null if missing/unparseable.
   * Observed claim shape: { role, sub (username), iat, exp }
   * NOTE: this does NOT verify the token's signature — that only happens
   * server-side. This is for routing/UI decisions only; the backend remains
   * the actual security boundary.
   */
  private getTokenClaims(): { role?: string; sub?: string; iat?: number; exp?: number } | null {
    const token = this.getToken();
    if (!token) return null;
    try {
      const payloadPart = token.split('.')[1];
      return JSON.parse(window.atob(payloadPart))
    } catch {
      return null;
    }
  }

  getTokenRole(): string | null {
    return this.getTokenClaims()?.role ?? null;
  }

  getTokenUsername(): string | null {
    return this.getTokenClaims()?.sub ?? null;
  }

  /**
   * Compares the stored username against the JWT's own `sub` claim. A
   * disagreement means the stored value was edited independently of the
   * token — i.e. local tampering. (Role no longer needs this check since
   * getRole() reads directly from the token now — nothing to diverge from.)
   */
  checkIntegrityMismatch(): string | null {
    const claims = this.getTokenClaims();
    if (!claims) return null;

    if (claims.sub && this.getUsername() !== claims.sub) {
      return 'Username mismatch';
    }

    const storedRole = this.getSession()?.userDetails?.role;
    if (claims.role && storedRole && storedRole !== claims.role) {
      return 'Role mismatch';
    }
    return null;
  }

  /**
   * Central handler for any forced logout: username tampering detected
   * locally, a role that's gone stale against the server, or an account the
   * server reports as inactive. Fires an audit log using the still-valid
   * token, bounded by a timeout so a hung request can't block the logout,
   * then clears the session and redirects to login with no returnUrl.
   */
  handleForcedLogout(reason: string): void {
    const token = this.getToken();
    const userId = this.getUserId();

    const description =
      `Forced Logout due to ${reason} - username: ${this.getUsername()}, ` +
      `token: role=${this.getTokenRole()}/user=${this.getTokenUsername()}`;

    const auditCall$ = this.auditLogService.saveAuditLog(
      'SECURITY',
      '/logout',
      description,
      'Security',
      token,
      userId
    ).pipe(
      timeout(this.AUDIT_LOG_TIMEOUT_MS),
      catchError(err => {
        console.error('[security] failed to log forced logout:', err);
        return of(null); // don't let a failed/slow audit log block the forced logout
      })
    );

    auditCall$.subscribe(() => {
      this.clearSession();
      this.router.navigate(['/features/login']);
    });
  }

  // ─── Profile sync ──────────────────────────────────────────────────────────

  fetchAndSyncProfile(): Observable<any> {
    const headers = new HttpHeaders({ Authorization: 'Bearer ' + this.getToken() });
    return this.http.get<any>(`${this.apiUrl}/api/v2/users/get-profile-details`, { headers }).pipe(
      tap(response => {
        if (response?.result) {
          // Account status: false = active, true = inactive per backend
          // convention — confirm this against a known-active AND a
          // known-deactivated test account before relying on it in prod.
          if (response.result.status === true) {
            this.handleForcedLogout('Account deactivated (server status)');
            return; // don't merge session data for a deactivated account
          }

          const current = this.getSession() ?? {};
          this.setSession({
            ...current,
            userDetails: {
              ...(current.userDetails ?? {}),
              ...response.result  // spreads id, username, email, role, status, imageUrl, createdAt, updatedAt
            }
          });

          // Staleness check: does the freshly-fetched, server-authoritative
          // role agree with what the current token claims? A disagreement
          // here means the role changed server-side after the token was
          // issued (e.g. a demotion), not local tampering.
          const serverRole = response.result?.role;
          const tokenRole = this.getTokenRole();
          if (serverRole && tokenRole && serverRole !== tokenRole) {
            this.handleForcedLogout('Role mismatch');
          }
        }
      })
    );
  }
}